// Gameplay constants. Values were read out of the official TETR.IO client
// (tetrio.js, build 20260714) so behaviour matches; the data layout is our own.
// Coordinate system: x grows right, y grows DOWN. Kick offsets use the same axes.

export type PieceType = "z" | "l" | "o" | "s" | "i" | "j" | "t";
export type Cell = PieceType | "g" | null; // g = garbage
export type Rotation = 0 | 1 | 2 | 3;

export const PIECES: PieceType[] = ["z", "l", "o", "s", "i", "j", "t"]; // bag order before shuffle

interface PieceDef {
  size: number; // bounding box edge
  dx: number; // box origin offset: cell (c, r) lands at (x + c - dx, ceil(y + r - dy))
  dy: number;
  spawn: [number, number][]; // cells in rotation 0, as [col, row]
}

const DEFS: Record<PieceType, PieceDef> = {
  z: { size: 3, dx: 1, dy: 1, spawn: [[0, 0], [1, 0], [1, 1], [2, 1]] },
  l: { size: 3, dx: 1, dy: 1, spawn: [[2, 0], [0, 1], [1, 1], [2, 1]] },
  o: { size: 2, dx: 0, dy: 1, spawn: [[0, 0], [1, 0], [0, 1], [1, 1]] },
  s: { size: 3, dx: 1, dy: 1, spawn: [[1, 0], [2, 0], [0, 1], [1, 1]] },
  i: { size: 4, dx: 1, dy: 1, spawn: [[0, 1], [1, 1], [2, 1], [3, 1]] },
  j: { size: 3, dx: 1, dy: 1, spawn: [[0, 0], [0, 1], [1, 1], [2, 1]] },
  t: { size: 3, dx: 1, dy: 1, spawn: [[1, 0], [0, 1], [1, 1], [2, 1]] },
};

export interface Shape {
  dx: number;
  dy: number;
  cells: [number, number][];
}

function rotateCW(cells: [number, number][], size: number): [number, number][] {
  return cells.map(([c, r]) => [size - 1 - r, c]);
}

// SHAPES[type][rotation] — rotations generated by spinning the bounding box clockwise,
// which reproduces the official matrices exactly.
export const SHAPES: Record<PieceType, Shape[]> = Object.fromEntries(
  PIECES.map((p) => {
    const def = DEFS[p];
    const rots: Shape[] = [];
    let cells = def.spawn;
    for (let r = 0; r < 4; r++) {
      rots.push({ dx: def.dx, dy: def.dy, cells: p === "o" ? def.spawn : cells });
      cells = rotateCW(cells, def.size);
    }
    return [p, rots];
  }),
) as Record<PieceType, Shape[]>;

type KickTable = Record<string, [number, number][]>;

// SRS+ (TETR.IO default). Keys are `${from}${to}`.
export const KICKS: KickTable = {
  "01": [[-1, 0], [-1, -1], [0, 2], [-1, 2]],
  "10": [[1, 0], [1, 1], [0, -2], [1, -2]],
  "12": [[1, 0], [1, 1], [0, -2], [1, -2]],
  "21": [[-1, 0], [-1, -1], [0, 2], [-1, 2]],
  "23": [[1, 0], [1, -1], [0, 2], [1, 2]],
  "32": [[-1, 0], [-1, 1], [0, -2], [-1, -2]],
  "30": [[-1, 0], [-1, 1], [0, -2], [-1, -2]],
  "03": [[1, 0], [1, -1], [0, 2], [1, 2]],
  "02": [[0, -1], [1, -1], [-1, -1], [1, 0], [-1, 0]],
  "13": [[1, 0], [1, -2], [1, -1], [0, -2], [0, -1]],
  "20": [[0, 1], [-1, 1], [1, 1], [-1, 0], [1, 0]],
  "31": [[-1, 0], [-1, -2], [-1, -1], [0, -2], [0, -1]],
};

export const I_KICKS: KickTable = {
  "01": [[1, 0], [-2, 0], [-2, 1], [1, -2]],
  "10": [[-1, 0], [2, 0], [-1, 2], [2, -1]],
  "12": [[-1, 0], [2, 0], [-1, -2], [2, 1]],
  "21": [[-2, 0], [1, 0], [-2, -1], [1, 2]],
  "23": [[2, 0], [-1, 0], [2, -1], [-1, 2]],
  "32": [[1, 0], [-2, 0], [1, -2], [-2, 1]],
  "30": [[1, 0], [-2, 0], [1, 2], [-2, -1]],
  "03": [[-1, 0], [2, 0], [2, 1], [-1, -2]],
  "02": [[0, -1]],
  "13": [[1, 0]],
  "20": [[0, 1]],
  "31": [[-1, 0]],
};

// T-spin corner checks per rotation: [cx, cy, frontA, frontB]. A corner is a
// "front" corner when the piece rotation equals frontA or frontB.
export const T_CORNERS: [number, number, number, number][] = [
  [-1, -1, 3, 0],
  [1, -1, 0, 1],
  [1, 1, 1, 2],
  [-1, 1, 2, 3],
];

export const SCORING = {
  SINGLE: 100, DOUBLE: 300, TRIPLE: 500, QUAD: 800,
  TSPIN_MINI: 100, TSPIN: 400,
  TSPIN_MINI_SINGLE: 200, TSPIN_SINGLE: 800,
  TSPIN_MINI_DOUBLE: 400, TSPIN_DOUBLE: 1200,
  TSPIN_MINI_TRIPLE: 800, TSPIN_TRIPLE: 1600,
  TSPIN_MINI_QUAD: 1600, TSPIN_QUAD: 2600,
  BACKTOBACK_MULTIPLIER: 1.5, COMBO: 50, ALL_CLEAR: 3500, SOFTDROP: 1, HARDDROP: 2,
};

// Base attack: index by lines cleared (0..4).
export const ATTACK = {
  normal: [0, 0, 1, 2, 4],
  spin: [0, 2, 4, 6, 10],
  mini: [0, 0, 1, 2, 4],
  BACKTOBACK_BONUS: 1,
  BACKTOBACK_BONUS_LOG: 0.8,
  COMBO_MINIFIER: 1,
  COMBO_MINIFIER_LOG: 1.25,
  COMBO_BONUS: 0.25,
};

export type SpinRule = "T-spins" | "all-mini+";

export interface GameOptions {
  seed: number;
  g: number; // rows per frame
  gincrease: number; // added to g per second after gmargin (frames)
  gmargin: number;
  locktime: number; // frames
  lockresets: number;
  nextcount: number;
  allow180: boolean;
  spinbonuses: SpinRule;
  b2bchaining: boolean;
  nolockout: boolean;
  hasgarbage: boolean;
  garbagespeed: number; // frames an incoming attack travels before it can enter
  garbagecap: number; // max lines tanked per piece
  garbagemultiplier: number;
  garbageincrease: number; // added to the multiplier per second after garbagemargin
  garbagemargin: number;
  messiness_change: number; // chance the hole moves between attacks
  allclear_garbage: number;
  levels: boolean; // blitz-style leveling
  levelspeed: number;
  levelgbase: number;
  levelgspeed: number;
  objective: { type: "none" } | { type: "lines"; count: number } | { type: "timed"; frames: number };
}

export const DEFAULT_OPTIONS: GameOptions = {
  seed: 1,
  g: 0.02,
  gincrease: 0,
  gmargin: 0,
  locktime: 30,
  lockresets: 15,
  nextcount: 5,
  allow180: true,
  spinbonuses: "all-mini+",
  b2bchaining: false,
  nolockout: false,
  hasgarbage: false,
  garbagespeed: 20,
  garbagecap: 8,
  garbagemultiplier: 1,
  garbageincrease: 0,
  garbagemargin: 0,
  messiness_change: 1,
  allclear_garbage: 10,
  levels: false,
  levelspeed: 1,
  levelgbase: 0.8,
  levelgspeed: 0.007,
  objective: { type: "none" },
};

export const BOARD_W = 10;
export const BOARD_H = 20; // visible
export const BUFFER = 20; // hidden rows above
export const BOARD_T = BOARD_H + BUFFER;
